import { ENUM_FALLBACK } from "@clipboard-health/contract-core";
import { type ShiftAuction, type ShiftModal } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type FullAddress } from "@src/appV2/lib/address/types";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import {
  type NegotiationHistoryAction,
  type NegotiationRateRole,
} from "@src/appV2/Negotiations/types";
import type {
  ShiftRateNegotiationRole,
  ShiftRateNegotiationStatus,
  WorkplaceShiftRateNegotiability,
} from "@src/appV2/OpenShifts/RateNegotiation/types";
import { type BreakPolicy } from "@src/appV2/Shifts/Shift/types";

import { calculateTotalPay } from "../utils/calculateShiftPay";
import { getShiftWorkDurationInHours } from "../utils/getShiftWorkDuration";
import {
  convertOfferToModalShiftOfferData,
  convertShiftAuctionBidToModalShiftAuctionBidData,
  convertShiftAuctionToModalShiftAuctionData,
} from "./convertOpenShiftToModalShiftData";
import { type Offer } from "./Open/types";
import {
  extractUnmetCriteria,
  extractWindowRestriction,
  extractWindowRestrictionScoreThreshold,
} from "./Open/window-restrictions";
import { resolveShiftType } from "./resolveShiftType";
import {
  type ModalShiftData,
  type ModalShiftRateNegotiationData,
  type ModalShiftSpecificationData,
  type ModalWorkplaceData,
  type ShiftAuctionBidDto,
} from "./types";

function convertWorkplaceToModalWorkplaceData(parameters: {
  workplace: ShiftModal["attributes"]["workplace"];
  workplaceFullAddress?: FullAddress;
}): ModalWorkplaceData {
  const { workplace, workplaceFullAddress } = parameters;

  return {
    id: workplace.id,
    attributes: {
      name: workplace.name,
      type: workplace.type,
      location: workplace.location,
      rating: workplace.rating,
      requiresLunchBreak: workplace.requiresLunchBreak ?? false,
      providesRestBreaks: workplace.providesRestBreaks ?? false,
      preventDoubleShifts: workplace.preventDoubleShifts ?? false,
      maxAllowedWorkHoursPerWeek: workplace.maxAllowedWorkHoursPerWeek ?? undefined,
      maxAllowedWorkConsecutiveHours: workplace.maxAllowedWorkConsecutiveHours ?? undefined,
      rateNegotiation: (workplace.rateNegotiationStatus ??
        "OFF") as WorkplaceShiftRateNegotiability,
      fullAddress: workplaceFullAddress,
      preShiftOrientationLengthInMinutes: workplace.preShiftOrientationLengthInMinutes ?? undefined,
    },
  };
}

function toModalShiftRateNegotiationLastOffer(parameters: {
  rateNegotiation: ShiftModal["attributes"]["rateNegotiation"];
  shiftWorkDurationInHours: number;
}): ModalShiftRateNegotiationData["lastOffer"] | undefined {
  const { rateNegotiation, shiftWorkDurationInHours } = parameters;

  const hourlyPay = rateNegotiation?.lastOffer?.payRate;

  if (!isDefined(hourlyPay)) {
    return undefined;
  }

  const { payAmount: totalPay, formattedPayAmount: formattedTotalPay } = calculateTotalPay({
    hourlyPayRate: hourlyPay,
    hoursWorked: shiftWorkDurationInHours,
  });

  return {
    hourlyPay,
    totalPay,
    formattedTotalPay,
    formattedHourlyPay: formatDollarsAsUsd(hourlyPay),
  };
}

function convertRateNegotiationToModalShiftRateNegotiationData(parameters: {
  rateNegotiation?: ShiftModal["attributes"]["rateNegotiation"];
  shiftWorkDurationInHours: number;
}): ModalShiftRateNegotiationData | undefined {
  const { rateNegotiation, shiftWorkDurationInHours } = parameters;

  if (!isDefined(rateNegotiation)) {
    return undefined;
  }

  const rateNegotiationHistory = Array.isArray(rateNegotiation.history)
    ? rateNegotiation.history
    : [];

  return {
    id: rateNegotiation.id,
    status: rateNegotiation.status as ShiftRateNegotiationStatus,
    lastOfferBy: rateNegotiation.lastOffer?.createdByUserRole as ShiftRateNegotiationRole,
    lastOffer: toModalShiftRateNegotiationLastOffer({
      rateNegotiation,
      shiftWorkDurationInHours,
    }),
    history: rateNegotiationHistory.map((historyItem) => ({
      ...historyItem,
      action: historyItem.action as NegotiationHistoryAction,
      createdByUserRole: historyItem.createdByUserRole as NegotiationRateRole,
    })),
  };
}

interface ConvertOpenShiftToModalShiftDataProps {
  shiftModal: ShiftModal;
  offer?: Offer;
  shiftAuction?: Pick<ShiftAuction, "id" | "attributes" | "relationships"> | undefined;
  shiftAuctionBid?: ShiftAuctionBidDto | undefined;
  workplaceFullAddress?: FullAddress;
  breakPolicy?: BreakPolicy;
  workerId?: string;
}

export function mapShiftModalData(props: ConvertOpenShiftToModalShiftDataProps): ModalShiftData {
  const { shiftModal, offer, workplaceFullAddress, breakPolicy, shiftAuction, shiftAuctionBid } =
    props;

  const requiresBreak =
    (shiftModal.attributes.workplace.requiresLunchBreak ?? false) && isDefined(breakPolicy);
  const shiftWorkDurationInHours = getShiftWorkDurationInHours(
    {
      start: shiftModal.attributes.shift.start,
      end: shiftModal.attributes.shift.end,
    },
    requiresBreak,
    requiresBreak ? (breakPolicy?.breakDurationInMinutes ?? 0) : 0
  );

  const windowRestriction = extractWindowRestriction(shiftModal.attributes.shift.bookability);
  const windowRestrictionScoreThreshold = extractWindowRestrictionScoreThreshold(
    shiftModal.attributes.shift.bookability
  );
  const unmetCriteria = extractUnmetCriteria(shiftModal.attributes.shift.bookability);

  const modalShiftAuction = convertShiftAuctionToModalShiftAuctionData(shiftAuction);

  return {
    id: shiftModal.id,
    workplace: convertWorkplaceToModalWorkplaceData({
      workplace: shiftModal.attributes.workplace,
      workplaceFullAddress,
    }),
    rateNegotiation: convertRateNegotiationToModalShiftRateNegotiationData({
      rateNegotiation: shiftModal.attributes.rateNegotiation,
      shiftWorkDurationInHours,
    }),
    offer: isDefined(modalShiftAuction)
      ? undefined
      : convertOfferToModalShiftOfferData(shiftWorkDurationInHours, offer),
    worker: shiftModal.attributes.worker,
    shiftAuction: modalShiftAuction,
    shiftAuctionBid: convertShiftAuctionBidToModalShiftAuctionBidData({
      shiftAuctionBid,
      shiftAuctionBidId: shiftAuction?.relationships.shiftAuctionBid?.data.id,
    }),
    breakPolicy,
    attributes: {
      type: resolveShiftType({
        isUrgent: shiftModal.attributes.shift.isUrgent,
        window: shiftModal.attributes.shift.window,
        priorityTill: shiftModal.attributes.shift.priorityTill,
      }),
      timeSlot: shiftModal.attributes.shift.timeSlot,
      start: shiftModal.attributes.shift.start,
      end: shiftModal.attributes.shift.end,
      durationInHours: shiftWorkDurationInHours,
      qualification: offer?.attributes.qualification ?? shiftModal.attributes.shift.qualification,
      isUrgent: shiftModal.attributes.shift.isUrgent,
      urgency: shiftModal.attributes.shift.urgency,
      window: shiftModal.attributes.shift.window,
      priorityTill: shiftModal.attributes.shift.priorityTill,
      isInterested: shiftModal.attributes.shift.isInterested,
      specifications: shiftModal.attributes.shift.specifications as ModalShiftSpecificationData[],
      windowRestriction: windowRestriction
        ? {
            windowName: windowRestriction,
            windowScoreThreshold: windowRestrictionScoreThreshold,
            ...(isDefined(unmetCriteria) && { unmetCriteria }),
          }
        : undefined,
      isInstantPay: shiftModal.attributes.shift.isInstantPay,
      isTransferrable: shiftModal.attributes.shift.isTransferrable,
      bookability: shiftModal.attributes.shift.bookability,
      ...(isDefined(shiftModal.attributes.shift.orientationShiftType) &&
        shiftModal.attributes.shift.orientationShiftType !== ENUM_FALLBACK && {
          orientationShiftType: shiftModal.attributes.shift.orientationShiftType,
        }),
    },
  };
}
